import { useEffect, useCallback, useRef } from 'react';

interface UseKeyboardNavigationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  enabled?: boolean;
}

export function useKeyboardNavigation({
  currentPage,
  totalPages,
  onPageChange,
  enabled = true,
}: UseKeyboardNavigationProps) {
  // Use refs to avoid recreating the handler on every render
  const currentPageRef = useRef(currentPage);
  const totalPagesRef = useRef(totalPages);
  const onPageChangeRef = useRef(onPageChange);
  const enabledRef = useRef(enabled);

  // Update refs when props change
  useEffect(() => {
    currentPageRef.current = currentPage;
    totalPagesRef.current = totalPages;
    onPageChangeRef.current = onPageChange;
    enabledRef.current = enabled;
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabledRef.current) return;

      // Only handle keyboard navigation when not in an input, textarea, select, or modal
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement ||
        // Don't interfere if any modal/dialog is open
        document.querySelector('[role="dialog"]') ||
        document.querySelector('.modal') ||
        // Don't interfere if any popup is open
        document.querySelector('[data-popup-open="true"]') ||
        // Don't interfere if focus is on navigation links or buttons
        document.activeElement?.tagName === 'A' ||
        document.activeElement?.getAttribute('role') === 'button'
      ) {
        return;
      }

      // Don't handle navigation keys if modifier keys are pressed (could be browser shortcuts)
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      // Be more conservative about when to handle arrow keys
      switch (event.key) {
        case 'ArrowLeft':
          if (currentPageRef.current > 1) {
            // Only prevent default if we're actually going to handle it
            event.preventDefault();
            event.stopPropagation();
            onPageChangeRef.current(currentPageRef.current - 1);
          }
          break;
        case 'ArrowRight':
          if (currentPageRef.current < totalPagesRef.current) {
            // Only prevent default if we're actually going to handle it
            event.preventDefault();
            event.stopPropagation();
            onPageChangeRef.current(currentPageRef.current + 1);
          }
          break;
        default:
          return;
      }
    },
    [] // No dependencies to prevent frequent re-creation
  );

  useEffect(() => {
    // Only add listener if enabled and we have valid pagination
    if (enabled && totalPages > 1) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, enabled, totalPages]); // Keep minimal dependencies
} 